// Alrendszer (üzemmód) felhasználóbarát megjelenítési nevei. A nyers kód (pl. "NAPLO")
// csak fallback, ha nincs barátságos név. Bővíthető új modul felvételekor.
export const MODE_NEVEK: Record<string, string> = {
  SULINFORM: 'Iskolai rendszer (Sulinform)',
  HOUSEHOLD: 'Háztartás',
  WEBSHOP: 'Webáruház',
  BILLIARD: 'Autokontír legacy (Billiárd)',
  MIRA: 'MIRA',
  CAESAR: 'Ügykezelő (CAESAR)',
  IKTATGATO: 'Iratkezelés (Iktatgató)',
  NAPLO: 'Könyvelés (Napló)',
  BER: 'Bérszámfejtés (Bér)',
  ESZKOZ: 'Tárgyi eszköz',
  SZAMLAZO: 'Számla és Kassza',
  INVENTARIO: 'Készletkezelés (Inventario)',
  GEPJARMU: 'Gépjármű',
  FITNESS: 'Edzés & táplálkozás (Fitness)',
  KLINIKUS: 'Kezelőorvosi munkatér',
  DEVCENTER: 'Fejlesztői konzol (Developer Control Center)',
};

export function modeNev(kod: string | null | undefined): string {
  if (!kod) return '';
  return MODE_NEVEK[kod] || kod;
}

// ── Ami NEM választható alrendszer ────────────────────────────────────────────────────────────
// Három „mód” van, ami csak névvel és arculattal rendelkezik, de az alrendszer-váltóban nincs
// helye. Mind a három a KÜLÖNVÁLT FITNESS PROGRAMHOZ tartozik (`fitness-app/`), amit ez az
// alkalmazás már nem szolgál ki:
//   FITNESS   – maga a program. Saját szervere, saját felülete és saját belépője van.
//   DEVCENTER – a program adminisztrációs és fejlesztői konzolja; onnan nyílik, nem innen.
//   KLINIKUS  – a kezelőorvosi munkatér; szintén a program része.
//
// MIÉRT MARAD BENT A NEVÜK ÉS AZ ARCULATUK. Mert a `feladatellatas.mode` mezőben és a
// böngésző tárolójában OTTMARADHAT a régi érték. Ha itt nem lenne mit kiírni, a felhasználó
// egy néma, üres képernyőt kapna; így viszont a váltóból eltűnnek, a tárolt érték pedig
// önjavít az intézmény fő alrendszerére (app.component: NEM_ONALLO_MODOK ág).
//
// A `menu`/`access` táblák FITNESS sorainak takarítását a
// `backend/scripts/fitness-menu-kivezetes.sql` végzi el.
export const NEM_ONALLO_MODOK = new Set(['FITNESS', 'DEVCENTER', 'KLINIKUS']);

/** Az alrendszer-váltóban felkínálható módok (a fentiek nélkül). */
export function valaszthatoModok<T extends { name?: string }>(modok: T[]): T[] {
  return (modok || []).filter((m) => !NEM_ONALLO_MODOK.has(String(m?.name || '').toUpperCase()));
}

// ── Böngészőfül: alrendszerenkénti cím + favicon ──────────────────────────────────────────────
// Az `ikon` az APP-IKONBÓL levezetett favicon (appicons/<mód>.png → `npm run gen-favicons` →
// assets/icon/mode/<mód>.png, 64×64). Vagyis a fül ikonja UGYANAZ, mint a telefonos app ikonja –
// egy forrásból (appicons/), nem kézzel másolgatva.
// Amelyik módhoz NINCS app-ikon, ahhoz menet közben rajzolunk (data-URI SVG): márkaszínű
// lekerekített négyzet + monogram. Így új modul is azonnal felismerhető fület kap, ikon nélkül is;
// ha később bekerül az appicons/-ba a képe, csak az `ikon` mezőt kell megadni.
interface ModeArc { szin: string; betuk: string; ikon?: string; }
const MODE_ARCOK: Record<string, ModeArc> = {
  SULINFORM: { szin: '#3880ff', betuk: 'SI', ikon: 'assets/icon/mode/sulinform.png' },
  NAPLO: { szin: '#0f766e', betuk: 'NA', ikon: 'assets/icon/mode/naplo.png' },   // szín = --naplo (global.scss)
  BER: { szin: '#c2410c', betuk: 'BÉ', ikon: 'assets/icon/mode/ber.png' },       // szín = --ber (ber-shared.scss)
  ESZKOZ: { szin: '#4338ca', betuk: 'TE', ikon: 'assets/icon/mode/eszkoz.png' },  // indigó – tárgyi eszköz
  SZAMLAZO: { szin: '#be123c', betuk: 'SZ', ikon: 'assets/icon/mode/szamlazo.png' },  // rózsapiros – számlázó
  INVENTARIO: { szin: '#b96800', betuk: 'IN', ikon: 'assets/icon/mode/inventario.png' },  // borostyán – készlet (a régi Inventario színe)
  GEPJARMU: { szin: '#1e5aa8', betuk: 'GJ', ikon: 'assets/icon/mode/gepjarmu.png' },  // közúti tábla-kék – gépjármű
  BILLIARD: { szin: '#1f6f43', betuk: 'BI', ikon: 'assets/icon/billiard.svg' },
  CAESAR: { szin: '#6d28d9', betuk: 'CA' },
  IKTATGATO: { szin: '#5b21b6', betuk: 'IK', ikon: 'assets/icon/mode/iktatgato.png' },  // lila – iratkezelés
  MIRA: { szin: '#0891b2', betuk: 'MI' },
  WEBSHOP: { szin: '#b45309', betuk: 'WS' },
  HOUSEHOLD: { szin: '#4d7c0f', betuk: 'HH' },
  FITNESS: { szin: '#2f8a52', betuk: 'FI', ikon: 'assets/icon/mode/fitness.png' },  // szín ≈ --fit akcentus
  KLINIKUS: { szin: '#1c5d8f', betuk: 'KO' },  // szín = --kl-akcent (klinikus.scss)
  DEVCENTER: { szin: '#5b8def', betuk: 'DC' }, // szín = --dc-accent DEVELOPMENT környezetben
};
const ALAP_ARC: ModeArc = { szin: '#3880ff', betuk: 'CR', ikon: 'assets/icon/mode/sulinform.png' };

function rajzoltFavicon(a: ModeArc): string {
  // A monogram hossza szerint kicsit kisebb betű, hogy a 2 karakter is elférjen.
  const meret = a.betuk.length > 1 ? 30 : 40;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">`
    + `<rect width="64" height="64" rx="14" fill="${a.szin}"/>`
    + `<text x="32" y="32" fill="#fff" font-family="Segoe UI,Roboto,Helvetica,Arial,sans-serif"`
    + ` font-size="${meret}" font-weight="700" text-anchor="middle" dominant-baseline="central">${a.betuk}</text>`
    + `</svg>`;
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

export function modeFavicon(kod: string | null | undefined): string {
  const a = MODE_ARCOK[String(kod || '')] || ALAP_ARC;
  return a.ikon || rajzoltFavicon(a);
}

// ── Tartalék márkaszínek ──────────────────────────────────────────────────────────────────────
// Az intézmény színei a `feladatellatas.color / color_secondary / color_shade` JSON-ökből jönnek,
// alrendszerenként. Ha egy ÚJ alrendszerhez ott nincs bejegyzés, a `--ion-color-primary` értéke
// érvénytelen lesz, és minden `color="primary"` elem – köztük a bal menü gombjai – fehér alapon
// fehér betűvel jelenik meg, vagyis a menü látszólag eltűnik. Ezért van tartalék: az alrendszer
// saját márkaszíne (MODE_ARCOK), amiből a világosabb/sötétebb árnyalatot itt keverjük ki.
function keverd(hex: string, cel: string, arany: number): string {
  const sz = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [r1, g1, b1] = sz(hex);
  const [r2, g2, b2] = sz(cel);
  const k = (a: number, b: number) => Math.round(a + (b - a) * arany).toString(16).padStart(2, '0');
  return `#${k(r1, r2)}${k(g1, g2)}${k(b1, b2)}`;
}

export function modeSzinek(kod: string | null | undefined): { primary: string; secondary: string; shade: string } {
  const alap = (MODE_ARCOK[String(kod || '')] || ALAP_ARC).szin;
  return { primary: alap, secondary: keverd(alap, '#ffffff', 0.35), shade: keverd(alap, '#000000', 0.3) };
}

// A böngészőfül teljes címe: „<alrendszer neve> · Cronomit”.
export function modeCim(kod: string | null | undefined): string {
  const n = modeNev(kod);
  return n ? `${n} · Cronomit` : 'Cronomit';
}
